import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { DocumentSort, GlobalThesisPayload } from '../types';

// Create an axios instance with the base URL from environment variables
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

// Add a request interceptor to include the token in requests
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = localStorage.getItem('token');
    const organization = localStorage.getItem('organization');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (organization && config.headers) {
      const org = JSON.parse(organization);
      config.headers['X-Organization-ID'] = org.id;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration and other errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    if (error.response) {
      const { status, data } = error.response;
      // Extract error message, handling both string and object formats
      let errorMessage = error.message;
      if (data) {
        // Check if data.message is an object (the new format from backend)
        if (typeof (data as any)?.message === 'object' && (data as any)?.message?.error) {
          // New format: { error: true, message: { error: "password_required", message: "...", filename: "..." }, status_code: 400 }
          const msgObj = (data as any).message;
          errorMessage = `${msgObj.error}: ${msgObj.message}`;
        } else if (typeof (data as any)?.detail === 'string') {
          errorMessage = (data as any).detail;
        } else if (typeof (data as any)?.message === 'string') {
          errorMessage = (data as any).message;
        } else if (typeof (data as any)?.detail === 'object') {
          // If detail is an object, try to extract message from it
          const detailObj = (data as any).detail;
          if (detailObj.error && detailObj.message) {
            errorMessage = `${detailObj.error}: ${detailObj.message}`;
          } else {
            errorMessage = detailObj?.message || JSON.stringify(detailObj);
          }
        }
      }

      if (status === 401) {
        // Token expired or invalid, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else if (status === 403) {
        const responseData = data as any;
        if (responseData?.message === "You do not have the required permissions to perform this action.") {
            try {
                const organization = localStorage.getItem('organization');
                let orgId;
                if (organization) {
                    const org = JSON.parse(organization);
                    orgId = org.id;
                }
                const updatedUser = await getCurrentUser(orgId);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                window.location.reload();
            } catch (refreshError) {
                console.error("Failed to refresh user data after permission error:", refreshError);
                // Fallback: log out the user.
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('organization');
                window.location.href = '/login';
            }
        } else {
            // Dispatch a specific event for access denied
            const event = new CustomEvent('accessDenied');
            window.dispatchEvent(event);
        }
      } else {
        // Dispatch a global error event for other errors
        const event = new CustomEvent('apiError', { detail: errorMessage });
        window.dispatchEvent(event);
      }
    }
    return Promise.reject(error);
  }
);

// Chat API functions
export const chatAPI = {
  // Send a chat message
  sendMessage: async (message: string, options?: { document_ids?: number[], project_id?: number }) => {
    const response = await api.post('/api/v1/chat/query', {
      message,
      document_ids: options?.document_ids || [],
      project_id: options?.project_id,
      context_limit: 1200,
    });
    return response.data;
  },

  // Get chat history
  getChatHistory: async (projectId?: string, limit: number = 50) => {
    const params: any = { limit };
    if (projectId) params.project_id = projectId;
    
    const response = await api.get('/api/v1/chat/history', { params });
    return response.data;
  },

  // Delete chat history for a project
  deleteChatHistory: async (projectId: string) => {
    const response = await api.delete(`/api/v1/chat/history/delete`, {
      params: { project_id: projectId },
    });
    return response.data;
  },

  // Get chat sessions
  getChatSessions: async (limit: number = 20, projectId?: string) => {
    const params: any = { limit };
    if (projectId) params.project_id = projectId;
    
    const response = await api.get('/api/v1/chat/sessions', { params });
    return response.data;
  },

  // Get messages from a specific session
  getSessionMessages: async (sessionId: number, limit: number = 50, offset: number = 0) => {
    const response = await api.get(`/api/v1/chat/sessions/${sessionId}/messages`, {
      params: { limit, offset },
    });
    return response.data;
  },
};

// Feedback API functions
export const feedbackAPI = {
  // Submit feedback for a message
  submitFeedback: async (messageId: number, feedbackType: string | null, note?: string) => {
    const response = await api.post(`/api/v1/chat/messages/${messageId}/feedback`, {
      feedback_type: feedbackType,
      note,
    });
    return response.data;
  },

  // Get feedback for a message
  getFeedback: async (messageId: number) => {
    const response = await api.get(`/api/v1/chat/messages/${messageId}/feedback`);
    return response.data;
  },

  // Delete feedback for a message
  deleteFeedback: async (messageId: number) => {
    const response = await api.delete(`/api/v1/chat/messages/${messageId}/feedback`);
    return response.data;
  },
};

// Processing API functions
export const processingAPI = {
  // Process all pending documents for current user
  processAllPending: async () => {
    const response = await api.post('/api/v1/processing/process-all-pending');
    return response.data;
  },

  // Get processing statistics
  getStats: async () => {
    const response = await api.get('/api/v1/processing/stats');
    return response.data;
  },

  // Generate embeddings for specific document
  generateEmbeddings: async (documentId: string) => {
    const response = await api.post(`/api/v1/processing/documents/${documentId}/generate-embeddings`);
    return response.data;
  },

  // Get embedding status for specific document
  getEmbeddingStatus: async (documentId: string) => {
    const response = await api.get(`/api/v1/processing/documents/${documentId}/embedding-status`);
    return response.data;
  },
};

// Auth API functions
export const authAPI = {
  forgotPassword: async (email: string) => {
    const frontendUrl = encodeURIComponent(window.location.origin);
    return api.post(`/api/v1/auth/forgot-password?frontend_url=${frontendUrl}`, { email });
  },

  resetPassword: async (data: {
    token: string;
    new_password: string;
    confirm_password: string;
  }) => {
    return api.post('/api/v1/auth/reset-password', data);
  },
};

export default api;


export const getProjects = async (params?: {
  page?: number;
  size?: number;
  sort?: string;
  is_shared?: boolean;
  stage?: [string];
  priority?: [string];
  is_private?: boolean;
  search?: string;
}) => {
  try {
    const response = await api.get('/api/v1/projects/', {
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
          const value = params[key as keyof typeof params];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProjectById = async (projectId: string) => {
  try {
    const response = await api.get(`/api/v1/projects/${projectId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createProject = async (projectData: {
  name: string;
  description?: string;
  lifecycle?: string;
  stage?: string;
  priority?: string;
  owner_id?: string;
  deal_id?: string;
  is_private?: boolean;
}) => {
  try {
    const response = await api.post('/api/v1/projects/', projectData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteProject = async (projectId: string) => {
  try {
    const response = await api.delete(`/api/v1/projects/${projectId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateProject = async (
  projectId: string,
  projectData: {
    name?: string;
    description?: string;
    stage?: string;
    priority?: string;
    owner_id?: string;
    is_private?: boolean;
    tags?: string[];
  }
) => {
  try {
    const response = await api.put(`/api/v1/projects/${projectId}`, projectData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProjectStageDescriptions = async () => {
  try {
    const response = await api.get('/api/v1/projects/status/descriptions');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProjectUsers = async (
  project_id: string,
  params?: {
    page?: number;
    size?: number;
  }
) => {
  try {
    const response = await api.get(`/api/v1/projects/${project_id}/team`, { params });
    const members = response.data.members || response.data || [];
    const total = response.data.total || members.length;
    return { members, total };
  } catch (error) {
    throw error;
  }
};
export const getOrganizationUsers = async (
  organization_id: string,
  params?: {
    role?: string;
    is_active?: boolean;
    search?: string;
    page?: number; // Add page parameter
    size?: number; // Add size parameter
  }
) => {
  try {
    const response = await api.get(`/api/v1/organizations/${organization_id}/members`, {
      params: params,
    });
    const members = response.data.members || response.data || [];
    const total = response.data.total || members.length;
    return { members, total };
  } catch (error) {
    throw error;
  }
};

export const createUser = async (userData: {
  email: string;
  password?: string;
  organization_id: string;
}) => {
  try {
    const response = await api.post('/api/v1/users/', userData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteUser = async (userId: string) => {
  try {
    const response = await api.delete(`/api/v1/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUser = async (
  userId: string,
  userData: {
    name?: string;
    email?: string;
    old_password?: string;
    new_password?: string;
    organization_role?: string;
    department_id?: number;
    permission_ids?: string[];
  }
) => {
  try {
    const response = await api.put(`/api/v1/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCurrentUser = async (org_id?: string) => {
  try {
    const params = org_id ? { org_id } : {};
    const response = await api.get('/api/v1/users/me', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const inviteUser = async (organizationId: string, userData: { email: string; role: string; department_id?: number }) => {
  try {
    const frontendUrl = encodeURIComponent(window.location.origin);
    const response = await api.post(`/api/v1/organizations/${organizationId}/invite?resend=true&frontend_url=${frontendUrl}`, userData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createFolderInProject = async (projectId: string, folderData: { name: string }) => {
  try {
    const response = await api.post(`/api/v1/projects/${projectId}/folders`, folderData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addUserToProject = async (projectData: {
  project_id: string;
  user_id: string;
}) => {
  try {
    const response = await api.put(
      `/api/v1/projects/${projectData.project_id}/team/${projectData.user_id}`,
      { project_id: projectData.project_id }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeUserFromProject = async (projectData: {
  project_id: string;
  user_id: string;
}) => {
  try {
    const response = await api.delete(`/api/v1/projects/${projectData.project_id}/team/${projectData.user_id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadFileToProject = async (projectId: string, files: File[], documentType: string, password?: string) => {
  const uploadPromises = files.map(file => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('type', documentType);
    formData.append('project_id', projectId);
    if (password) {
      formData.append('password', password);
    }
    return api.post(`/api/v1/documents/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  });

  try {
    const responses = await Promise.all(uploadPromises);
    return responses.map(response => response.data);
  } catch (error) {
    throw error;
  }
};

export const uploadFileUnified = async (files: File[], documentType: string, password?: string) => {
  const uploadPromises = files.map(file => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('document_type', documentType);
    if (password) {
      formData.append('password', password);
    }
    return api.post(`/api/v1/unified-documents/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  });

  try {
    const responses = await Promise.all(uploadPromises);
    return responses.map(response => response.data);
  } catch (error) {
    throw error;
  }
};

export const getProjectFiles = async (
  projectId: string,
  params?: {
    page?: number; // Changed from skip to page
    size?: number;
    sort?: DocumentSort;
    search?: string;
  }
) => {
  try {
    const response = await api.get(`/api/v1/documents/`, { params: { ...params, project_id: projectId } });
    const documents = response.data.documents || response.data.files || [];
    const total = response.data.total || documents.length;
    return { files: documents, total };
  } catch (error) {
    throw error;
  }
};


export const getProjectStats = async (projectId: string) => {
  try {
    const response = await api.get(`/api/v1/projects/${projectId}/stats`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProjectFolders = async (
  projectId: string,
  params?: {
    skip?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }
) => {
  try {
    const response = await api.get(`/api/v1/projects/${projectId}/folders`, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteFile = async (fileId: string) => {
  try {
    const response = await api.delete(`/api/v1/documents/${fileId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkDeleteFiles = async (documentIds: string[]) => {
  try {
    const response = await api.post(`/api/v1/documents/bulk-delete`, { document_ids: documentIds });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteFolder = async (projectId: string, folderId: string) => {
  try {
    const response = await api.delete(`/api/v1/projects/${projectId}/folders/${folderId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const downloadFile = async (fileId: string, fileName: string) => {
  try {
    const response = await api.get(`/api/v1/documents/${fileId}/download`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    throw error;
  }
};

export const bulkDownloadFiles = async (documentIds: string[]) => {
  try {
    const response = await api.post(`/api/v1/documents/bulk-download`, { document_ids: documentIds }, {
      responseType: 'blob', // Important for downloading files
    });

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'documents.zip'; // Default filename

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url); // Clean up the URL object
  } catch (error) {
    throw error;
  }
};

export const downloadFolder = async (projectId: string, folderId: string, folderName: string) => {
  try {
    const response = await api.get(`/api/v1/projects/${projectId}/download/folder/${folderId}`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${folderName}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    throw error;
  }
};


// ... (rest of the file)

export const getDocuments = async (params?: {
  page?: number;
  size?: number;
  sort?: DocumentSort;
  search?: string;
  type?: string[];
  project_id?: string;
  processing_status?: string[];
}) => {
  try {
    const response = await api.get('/api/v1/documents/', { 
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllDocuments = async (params?: {
  page?: number;
  size?: number;
  sort?: DocumentSort;
  search?: string;
  type?: string[];
  project_id?: string;
  processing_status?: string[];
}) => {
  try {
    const response = await api.get('/api/v1/all-documents/all-documents', { 
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDocumentStats = async () => {
  try {
    const response = await api.get('/api/v1/all-documents/all-documents/stats');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllProjectDocuments = async (params?: {
  page?: number;
  size?: number;
  sort?: DocumentSort;
  project_id?: string;
  type?: string[];
  processing_status?: string[];
}) => {
  try {
    const response = await api.get('/api/v1/all-documents/all-project-documents', {
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
          const value = params[key as keyof typeof params];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllDealDocuments = async (params?: {
  page?: number;
  size?: number;
  sort?: DocumentSort;
  deal_id?: string;
  type?: string[];
  processing_status?: string[];
  search?: string;
}) => {
  try {
    const response = await api.get('/api/v1/all-documents/all-deal-documents', {
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
          const value = params[key as keyof typeof params];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};


export const getDocumentById = async (documentId: string) => {
  try {
    const response = await api.get(`/api/v1/documents/${documentId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getOrganizations = async () => {
  try {
    const response = await api.get('/api/v1/organizations/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAuditLogs = async (params?: {
  page?: number;
  size?: number;
  user_id?: string;
  entity_type?: string;
  action?: string;
  search?: string;
}) => {
  try {
    const response = await api.get('/api/v1/audit/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getOrganizationRoles = async () => {
  try {
    const response = await api.get('/api/v1/organizations/roles');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getOrganizationSettings = async (organizationId: string) => {
  try {
    const response = await api.get(`/api/v1/organizations/${organizationId}/settings`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateOrganizationSettings = async (organizationId: string, settingsData: any) => {
  try {
    const response = await api.put(`/api/v1/organizations/${organizationId}/settings`, settingsData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createDeal = async (dealData: any) => {
  try {
    const response = await api.post('/api/v1/deals/', dealData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDealById = async (dealId: string) => {
  try {
    const response = await api.get(`/api/v1/deals/${dealId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteDeal = async (dealId: string) => {
  try {
    const response = await api.delete(`/api/v1/deals/${dealId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDeals = async (params: {
  page?: number;
  size?: number;
  search?: string;
  deal_type?: string[];
  status?: string[];
  sector?: string[];
  region?: string[];
  sort?: string;
}) => {
  try {
    const response = await api.get('/api/v1/deals/', {
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDealQuestions = async (dealId: string) => {
  try {
    const response = await api.get(`/api/v1/deals/${dealId}/questions`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const generateQuestionAnswer = async (dealId: string, questionId: string) => {
  try {
    const response = await api.post(`/api/v1/deals/${dealId}/questions/${questionId}/generate-answer`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const generateAllAnswers = async (dealId: string) => {
  try {
    const response = await api.post(`/api/v1/deals/${dealId}/questions/generate-all-answers`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDealFinancials = async (dealId: string) => {
  try {
    const response = await api.get(`/api/v1/deals/${dealId}/financials`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDealAIScore = async (dealId: string) => {
  try {
    const response = await api.get(`/api/v1/deal-scoring/${dealId}/score`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDealFlags = async (dealId: string) => {
  try {
    const response = await api.get(`/api/v1/deals/${dealId}/flags`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDealDocuments = async (dealId: string, params?: { sort?: DocumentSort }) => {
  try {
    const response = await api.get(`/api/v1/deals/${dealId}/documents`, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const dealsApi = {
  scoreDeal: async (dealId: string, fundId?: string, forceRescore: boolean = false, changeReason: string = 'Auto-scoring after document upload') => {
    try {
      const body: any = {
        force_rescore: forceRescore,
        change_reason: changeReason,
      };
      if (fundId && fundId !== 'auto') {
        body.fund_id = fundId;
      }
      const response = await api.post(`/api/v1/deal-scoring/${dealId}/score`, body);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getDealScore: async (dealId: string) => {
    try {
      const response = await api.get(`/api/v1/deal-scoring/${dealId}/score`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  uploadDocumentToDeal: async (dealId: string, file: File, documentType: string, password?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    if (password) {
      formData.append('password', password);
    }
    try {
      const response = await api.post(`/api/v1/deals/${dealId}/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  uploadDocumentFromIntegration: async (dealId: string, toolName: string, fileIds: string[], documentType: string) => {
    try {
      const response = await api.post(`/api/v1/deals/${dealId}/documents/upload_from_integration`, {
        tool_name: toolName,
        file_ids: fileIds,
        document_type: documentType,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  linkDocumentsToDeal: async (dealId: string, documentIds: string[]) => {
    try {
      const response = await api.post(`/api/v1/deals/${dealId}/documents/link`, {
        document_ids: documentIds,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  getDealDocument: async (dealId: string, documentId: string) => {
    try {
      const response = await api.get(`/api/v1/deals/${dealId}/documents/${documentId}`);
      return response;
    } catch (error) {
      throw error;
    }
  },
  getAllDocuments: async (params?: { sort?: DocumentSort }) => {
    try {
      const response = await api.get(`/api/v1/deals/documents`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
export const uploadDocumentFromIntegrationToProject = async (toolName: string, fileIds: string[], documentType: string, projectId: string) => {
  try {
    const response = await api.post(`/api/v1/documents/upload/integration`, {
      tool_name: toolName,
      file_ids: fileIds,
      document_type: documentType,
      project_id:projectId
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadUnifiedDocumentFromIntegration = async (toolName: string, fileIds: string[], documentType: string) => {
  try {
    const response = await api.post(`/api/v1/unified-documents/upload/integration`, {
      tool_name: toolName,
      file_ids: fileIds,
      document_type: documentType,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getFirmProfile = async () => {
 try {
   const response = await api.get('/api/v1/investment-thesis/firm-profile');
   return response.data;
 } catch (error) {
   throw error;
 }
};

export const updateFirmProfile = async (profileData: any) => {
 try {
   const response = await api.put('/api/v1/investment-thesis/firm-profile', profileData);
   return response.data;
 } catch (error) {
   throw error;
 }
};

export const createFirmProfile = async (profileData: any) => {
  try {
    const response = await api.post('/api/v1/investment-thesis/firm-profile', profileData);
    return response.data;
  } catch (error) {
    throw error;
  }
};
export const getFunds = async (params?: {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
  sort?: string;
}) => {
  try {
    const response = await api.get('/api/v1/funds/', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createFund = async (fundData: {
  name: string;
  total_commitments: number;
  vintage: number;
  status: string;
}) => {
  try {
    const response = await api.post('/api/v1/funds/', fundData);
    return response.data;
  } catch (error) {
    throw error;
  }
};
export const deleteFund = async (fundId: string) => {
  try {
    const response = await api.delete(`/api/v1/funds/${fundId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getGlobalThesis = async () => {
 try {
   const response = await api.get('/api/v1/investment-thesis/global');
   return response.data;
 } catch (error) {
   throw error;
 }
};

export const createGlobalThesis = async (thesisData: any) => {
 try {
   const response = await api.post('/api/v1/investment-thesis/global', thesisData);
   return response.data;
 } catch (error) {
   throw error;
 }
};

export const updateGlobalThesis = async (thesisId: string, thesisData: GlobalThesisPayload) => {
 try {
   const response = await api.put(`/api/v1/investment-thesis/global/${thesisId}`, thesisData);
   return response.data;
 } catch (error) {
   throw error;
 }
};

export const getGlobalThesisVersions = async () => {
 try {
   const response = await api.get('/api/v1/investment-thesis/global/versions');
   return response.data;
 } catch (error) {
   throw error;
 }
};

export const getCommonSectors = async () => {
 try {
   const response = await api.get('/api/v1/investment-thesis/reference/common-sectors');
  return response.data;
} catch (error) {
  throw error;
}
};

export const getGlobalThesisVersionById = async (versionId: string) => {
  try {
    const response = await api.get(`/api/v1/investment-thesis/versions/${versionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const rollbackToVersion = async (thesisId: string, versionId: string) => {
 try {
   const response = await api.post(`/api/v1/investment-thesis/${thesisId}/versions/${versionId}/rollback`);
   return response.data;
 } catch (error) {
   throw error;
 }
};

export const getCommonRegions = async () => {
 try {
   const response = await api.get('/api/v1/investment-thesis/reference/common-regions');
   return response.data;
 } catch (error) {
   throw error;
 }
};

export const getFundTheses = async () => {
    try {
      const response = await api.get('/api/v1/investment-thesis/fund');
      return response.data;
    } catch (error) {
      throw error;
    }
};

export const createFundThesis = async (fundId: string, thesisData: any) => {
    try {
      const response = await api.post(`/api/v1/investment-thesis/fund/${fundId}`, thesisData);
      return response.data;
    } catch (error) {
      throw error;
    }
};

export const updateFundThesis = async (fundId: string, thesisId: string, thesisData: any) => {
    try {
      const response = await api.put(`/api/v1/investment-thesis/fund/${fundId}/${thesisId}`, thesisData);
      return response.data;
    } catch (error) {
      throw error;
    }
};
export const getFundThesisVersions = async (thesisId: string) => {
    try {
      const response = await api.get(`/api/v1/investment-thesis/${thesisId}/versions`);
      return response.data;
    } catch (error) {
      throw error;
    }
};

export const getInvestmentThesisVersions = async (thesisId: string) => {
    try {
      const response = await api.get(`/api/v1/investment-thesis/${thesisId}/versions`);
      return response.data;
    } catch (error) {
      throw error;
    }
};
export const getInvestmentThesisFilters = async (thesisId: string) => {
    try {
      const response = await api.get(`/api/v1/investment-thesis/${thesisId}/filters`);
      return response.data;
    } catch (error) {
      throw error;
    }
};

export const createInvestmentThesisFilter = async (thesisId: string, filterData: any) => {
    try {
      const response = await api.post(`/api/v1/investment-thesis/${thesisId}/filters`, filterData);
      return response.data;
    } catch (error) {
      throw error;
    }
};

export const getInvestmentThesisFilterById = async (filterId: string) => {
    try {
      const response = await api.get(`/api/v1/investment-thesis/filters/${filterId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
};

export const updateInvestmentThesisFilter = async (filterId: string, filterData: any) => {
    try {
      const response = await api.put(`/api/v1/investment-thesis/filters/${filterId}`, filterData);
      return response.data;
    } catch (error) {
      throw error;
    }
};

export const deleteInvestmentThesisFilter = async (filterId: string) => {
    try {
      const response = await api.delete(`/api/v1/investment-thesis/filters/${filterId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
};

export const getInvitationDetails = async (token: string) => {
  try {
    const response = await api.get(`/api/v1/organizations/invitations/${token}/details`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const acceptInvitation = async (token: string) => {
  try {
    const response = await api.post('/api/v1/organizations/invitations/accept', { token });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const declineInvitation = async (token: string, reason?: string) => {
  try {
    const response = await api.post('/api/v1/organizations/invitations/decline', { token, reason });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Structured Queries API functions
export const structuredQueriesAPI = {
  // Create a new structured query
  createStructuredQuery: async (projectId: string, queryData: {
    name: string;
    description?: string;
    original_query: string;
    query_type?: string;
    columns?: Array<{
      name: string;
      query: string;
      type?: string;
    }>;
  }) => {
    const response = await api.post(`/api/v1/structured-queries/projects/${projectId}/structured-queries`, queryData);
    return response.data;
  },

  // List all structured queries for a project
  getStructuredQueries: async (projectId: string) => {
    const response = await api.get(`/api/v1/structured-queries/projects/${projectId}/structured-queries`);
    return response.data;
  },

  // Get a specific structured query with all its data
  getStructuredQuery: async (queryId: string) => {
    const response = await api.get(`/api/v1/structured-queries/structured-queries/${queryId}`);
    return response.data;
  },

  // Run a structured query against all documents
  runStructuredQuery: async (queryId: string) => {
    const response = await api.post(`/api/v1/structured-queries/structured-queries/${queryId}/run`);
    return response.data;
  },

  // Run a specific query cell
  runQueryCell: async (queryId: string, cellData: {
    column_id: string;
    document_id: string;
    simple_mode?: boolean;
  }) => {
    const response = await api.post(`/api/v1/structured-queries/structured-queries/${queryId}/run-cell`, cellData);
    return response.data;
  },

  // Add a new column to structured query
  addQueryColumn: async (queryId: string, columnData: {
    name: string;
    query: string;
    type?: string;
  }) => {
    const response = await api.post(`/api/v1/structured-queries/structured-queries/${queryId}/add-column`, columnData);
    return response.data;
  },

  // Delete a structured query
  deleteStructuredQuery: async (queryId: string) => {
    const response = await api.delete(`/api/v1/structured-queries/structured-queries/${queryId}`);
    return response.data;
  },
};

// Authorize an integration
export const authorizeIntegration = async (toolName: string, frontendUri?: string) => {
  const params = frontendUri ? { frontendUri: frontendUri } : {};
  const response = await api.get(`/api/v1/integrations/authorize/${toolName}`, { params });
  return response.data;
};


export const getIntegrations = async () => {
  try {
    const response = await api.get('/api/v1/integrations/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteIntegration = async (integrationId: string) => {
  try {
    const response = await api.delete(`/api/v1/integrations/${integrationId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getIntegrationFiles = async (integrationName: string, folderId: string) => {
  try {
    const endpoint = `/api/v1/${integrationName}/files/list`;
    let params: any = {};

    if (integrationName === 'googledrive' || integrationName === 'box') {
      params = { folder_id: folderId };
    } else if (integrationName === 'dropbox') {
      params = { folder_path: folderId === 'root' ? '' : folderId };
    } else if (integrationName === 'onedrive') {
      params = { folder_id: folderId };
    }

    const response = await api.get(endpoint, { params });
    return response;
  } catch (error) {
    throw error;
  }
};

export const shareDocument = async (documentId: string, sharedWithId: string) => {
  try {
    const response = await api.post('/api/v1/shared-documents/share', {
      document_id: documentId,
      shared_with_id: sharedWithId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkShareDocuments = async (documentIds: string[], sharedWithId: string) => {
  try {
    const response = await api.post('/api/v1/shared-documents/bulk-share', {
      document_ids: documentIds,
      shared_with_id: sharedWithId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bulkCopyToProject = async (documentIds: string[], targetProjectId: string) => {
  try {
    const response = await api.post(`/api/v1/documents/bulk-copy-to-project`, {
      document_ids: documentIds,
      target_project_id: targetProjectId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getMembers = async () => {
    const organization = localStorage.getItem('organization');
    if (organization) {
        const org = JSON.parse(organization);
        // Pass is_active: true to filter for active users
        return getOrganizationUsers(org.id, { is_active: true });
    }
    return { members: [], total: 0 };
};

export const getSharedDocuments = async (params?: {
  page?: number;
  size?: number;
  sort?: DocumentSort;
  type?: string;
  processing_status?: string;
}) => {
  try {
    const response = await api.get('/api/v1/all-documents/all-shared-documents', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Multimodal Document API functions
export const multimodalAPI = {
  // Get images extracted from a document
  getDocumentImages: async (documentId: string, pageNumber?: number) => {
    const params = pageNumber ? { page_number: pageNumber } : {};
    const response = await api.get(`/api/v1/documents/${documentId}/images`, { params });
    return response.data;
  },

  // Get page thumbnails for a document
  getDocumentThumbnails: async (documentId: string, pageNumber?: number) => {
    const params = pageNumber ? { page_number: pageNumber } : {};
    const response = await api.get(`/api/v1/documents/${documentId}/thumbnails`, { params });
    return response.data;
  },

  // Process an existing document for multimodal content
  processDocumentMultimodal: async (documentId: string) => {
    const response = await api.post(`/api/v1/documents/${documentId}/process-multimodal`);
    return response.data;
  },
};

export const sharedFundsApi = {
  shareFund: async (shareData: { fund_id: string; shared_with_id: string }) => {
    const response = await api.post('/api/v1/shared-funds/share', shareData);
    return response.data;
  },

  getSharedWithUsers: async (fundId: string) => {
    const response = await api.get(`/api/v1/shared-funds/shared-with/${fundId}`);
    return response.data;
  },

  unshareFund: async (sharedFundId: string) => {
    const response = await api.delete(`/api/v1/shared-funds/unshare/${sharedFundId}`);
    return response.data;
  },
};

export const sharedDealsApi = {
  shareDeal: async (shareData: { deal_id: string; shared_with_id: string }) => {
    const response = await api.post('/api/v1/shared-deals/share', shareData);
    return response.data;
  },

  getSharedWithUsers: async (dealId: string) => {
    const response = await api.get(`/api/v1/shared-deals/shared-with/${dealId}`);
    return response.data;
  },

  unshareDeal: async (sharedDealId: string) => {
    const response = await api.delete(`/api/v1/shared-deals/unshare/${sharedDealId}`);
    return response.data;
  },
};

export const getDDQQuestions = async (projectId: string, status?: string, params?: { page?: number; size?: number; }) => {
  try {
    const apiParams: any = { ...params };
    if (status && status !== 'all') {
      apiParams.status = status;
    }
    const response = await api.get(`/api/v1/ddq/projects/${projectId}/questions`, { params: apiParams });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateDDQQuestion = async (
  questionId: string,
  assigneeId?: string,
  reviewerId?: string,
  status?: string
) => {
  try {
    const payload: any = {};
    if (assigneeId !== undefined) payload.assignee_id = assigneeId;
    if (reviewerId !== undefined) payload.reviewer_id = reviewerId;
    if (status) payload.status = status;

    const response = await api.put(`/api/v1/ddq/questions/${questionId}`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllDDQQuestions = async (statuses?: string[], aiGenerated?: boolean, createdAt?: string, search?: string, page?: number, size?: number, tags?: string[]) => {
  try {
    const params: any = {};
    if (statuses && statuses.length > 0) {
      params.status = statuses;
    }
    if (aiGenerated !== undefined) {
      params.ai_generated = aiGenerated;
    }
    if (createdAt) {
      params.created_at = createdAt;
    }
    if (search) {
      params.search = search;
    }
    if (page) {
      params.page = page;
    }
    if (size) {
      params.size = size;
    }
    if (tags && tags.length > 0) {
      params.tags = tags;
    }
    const response = await api.get('/api/v1/ddq/questions/', {
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
          const value = params[key as keyof typeof params];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateDDQAnswer = async (answerId: string, answerText: string) => {
  try {
    const response = await api.put(`/api/v1/ddq/answers/${answerId}`, {
      answer_text: answerText,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDDQQuestionById = async (questionId: string) => {
  try {
    const response = await api.get(`/api/v1/ddq/questions/${questionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const rollbackAnswerVersion = async (answerId: string, versionId: string) => {
  try {
    const response = await api.post(`/api/v1/ddq/answers/${answerId}/versions/${versionId}/rollback`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createDDQAnswer = async (answerData: {
  answer_text: string;
  status: string;
  question_id: string;
  sources: any[];
  ai_generated: boolean;
}) => {
  try {
    const response = await api.post('/api/v1/ddq/answers/', answerData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAnswerVersions = async (answerId: string) => {
  try {
    const response = await api.get(`/api/v1/ddq/answers/${answerId}/versions`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllDdqDocuments = async (params?: {
  page?: number;
  size?: number;
  sort?: DocumentSort;
  project_id?: string;
  type?: string[];
  processing_status?: string[];
}) => {
  try {
    const response = await api.get('/api/v1/all-documents/all-ddq-documents', {
      params,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
          const value = params[key as keyof typeof params];
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, v));
          } else if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const exportDdqDocument = async (documentId: string, format: 'docx' | 'xlsx' | 'pdf', ddq_export_history_id?: string) => {
  try {
    const apiFormat = format === 'docx' ? 'word' : format === 'xlsx' ? 'excel' : format;
    const params: any = { format: apiFormat };
    if (ddq_export_history_id) {
      params.ddq_export_history_id = ddq_export_history_id;
    }
    const response = await api.post(`/api/v1/documents/${documentId}/export`, null, {
      params,
      responseType: 'blob',
    });

    // Extract filename from content-disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `ddq-export.${format}`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
      if (filenameMatch && filenameMatch.length > 1) {
        filename = filenameMatch[1];
      }
    }

    // Create a URL for the blob and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, message: 'File downloaded successfully.' };
  } catch (error) {
    throw error;
  }
};

export const getExportHistory = async (params?: {
  document_id?: string;
  page?: number;
  size?: number;
}) => {
  try {
    const response = await api.get('/api/v1/export-history', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getDepartments = async () => {
  try {
    const response = await api.get('/api/v1/departments/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createDepartment = async (departmentData: { name: string }) => {
  try {
    const response = await api.post('/api/v1/departments/', departmentData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getTags = async () => {
  try {
    const response = await api.get('/api/v1/tags/');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateQuestion = async (questionId: string, tags: string[]) => {
  try {
    const response = await api.put(`/api/v1/ddq/questions/${questionId}`, {
      tags,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPermissions = async (orgId: string) => {
  try {
    const response = await api.get(`/api/v1/permissions/${orgId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};
